import type { TokenResponse } from '@baseline/shared';

// The one way the web app talks to the gateway (plan Phase 10): JSON over fetch, the access
// token in memory, and on a 401 one refresh through the httpOnly cookie, then one retry.

export const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

/** `{ error: { code, message, details? } }` from any service (CLAUDE.md section 7). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
const signedOutListeners = new Set<() => void>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Called when a refresh fails: the session is over and the app shows the login page. */
export function onSignedOut(listener: () => void): () => void {
  signedOutListeners.add(listener);
  return () => signedOutListeners.delete(listener);
}

async function errorOf(res: Response): Promise<ApiError> {
  const body: unknown = await res.json().catch(() => null);
  if (body && typeof body === 'object' && 'error' in body) {
    const err = (body as { error: { code?: unknown; message?: unknown; details?: unknown } }).error;
    return new ApiError(
      res.status,
      typeof err.code === 'string' ? err.code : 'UNKNOWN',
      typeof err.message === 'string' ? err.message : 'Something went wrong.',
      err.details,
    );
  }
  return new ApiError(res.status, 'UNKNOWN', 'Baseline couldn’t reach the server. Try again.');
}

/** One refresh at a time, shared by every request that got a 401 meanwhile. */
export function refreshAccessToken(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return false;
      const body = (await res.json()) as TokenResponse;
      accessToken = body.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** Sent as JSON, unless it is FormData. */
  body?: unknown;
  signal?: AbortSignal;
}

function send(path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { accept: 'application/json' };
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) body = opts.body;
  else if (opts.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;
  return fetch(`${API_BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body,
    signal: opts.signal,
    credentials: 'include',
  });
}

/** The raw response after the 401 → refresh → retry-once dance. Throws ApiError otherwise. */
export async function apiResponse(path: string, opts: RequestOptions = {}): Promise<Response> {
  let res = await send(path, opts);
  if (res.status === 401 && !path.startsWith('/auth/')) {
    if (await refreshAccessToken()) res = await send(path, opts);
    if (res.status === 401) {
      accessToken = null;
      signedOutListeners.forEach((l) => l());
    }
  }
  if (!res.ok) throw await errorOf(res);
  return res;
}

/** JSON in, typed JSON out (types from `@baseline/shared`). */
export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await apiResponse(path, opts);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
